import TextStyle from '@tiptap/extension-text-style';
import { Underline } from '@tiptap/extension-underline';
import { EditorProps } from '@tiptap/pm/view';
import StarterKit from '@tiptap/starter-kit';
import { useVideoService } from '@yarmill/utils';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { FormControlVariant } from '../helpers';
import { css, styled } from '../theme-provider';
import { YtdBold } from './extensions/bold';
import { Highlight, RICHTEXT_HIGHLIGHT_COLORS } from './extensions/highlight';
import { InlineSuggestion } from './extensions/inline-suggestion';
import { Link } from './extensions/link';
import { Placeholder } from './extensions/placeholder';
import {
  PlannerEvents,
  createLazyRichtextPlannerEvents,
} from './extensions/planner-events';
import { VideoLink } from './extensions/video-link';
import { MenuBar } from './menu-bar';
import { RichTextEditorProps } from './types';
import { useEditor } from './use-editor';

const StyledRichText = styled.div<{
  readonly asInput?: boolean;
  readonly variant?: FormControlVariant;
  readonly disabled?: boolean;
  readonly $hasPlannerEvents?: boolean;
  readonly $hasMultilinePlannerEvents?: boolean;
  readonly 'aria-readonly'?: boolean;
}>`
  max-width: 100%;
  position: relative;
  color: ${({ theme }) => theme.color.text};
  font-size: ${({ theme }) => theme.text.appearance.task13.fontSize};
  line-height: ${({ theme }) => theme.text.appearance.task13.lineHeight};
  font-family: ${({ theme }) => theme.text.font.default};
  border-radius: 4px;

  ${({ disabled }) =>
    disabled &&
    css`
      background-color: #f5f5f5;
    `};

  ${({ theme }) =>
    theme.name === 'main' &&
    css`
      color: inherit;
      font-size: inherit;
      font-style: inherit;
      font-weight: inherit;
      line-height: inherit;
      border-radius: 0;

      &:focus-within {
        color: inherit;
      }
    `}

  ${({ asInput }) =>
    asInput &&
    css`
      border: 1px solid rgb(206, 212, 218);
      border-radius: 4px;

      & > div:empty {
        padding: 0.375rem 0.75rem;
        height: calc((13px * 1.5) + 2 * 0.375rem);
      }

      :focus-within {
        border-color: rgb(74, 144, 226);
      }
    `}

  ${({ variant }) =>
    variant === FormControlVariant.big &&
    css`
      border: 1px solid #979797;
      border-radius: 8px;
    `}

  .ProseMirror {
    width: 100%;
    max-height: 30em;
    overflow-x: hidden;
    overflow-y: auto;
    border-radius: 4px;
    word-break: break-word;

    ${({ asInput }) =>
      asInput &&
      css`
        padding: 0.375rem 0.75rem;
        padding-right: 28px;
      `}

    ${({ theme }) =>
      theme.name === 'main' &&
      css`
        border-radius: 0;
      `};

    ${({ variant }) =>
      variant === FormControlVariant.big &&
      css`
        font-size: 18px;
        min-height: 65px;
        padding: 15px;
      `}

    :focus {
      outline: none;
    }

    br + .ProseMirror-trailingBreak {
      display: none;
    }

    p {
      margin: 0;
      min-height: ${({ theme }) => theme.text.appearance.task13.lineHeight};

      :empty {
        :after {
          content: "\\00A0";
        }
      }
    }

    ol,
    ul {
      margin: 0;
      padding-left: 32px;
    }

    a {
      cursor: pointer;
      color: rgb(77, 161, 255);
      text-decoration: underline;

      .highlight {
        color: rgb(77, 161, 255);
      }
    }

    .highlight {
      color: #4a4a4a;
      padding: 0.1em 0;
      background-color: ${RICHTEXT_HIGHLIGHT_COLORS.orange};

      &-orange {
        background-color: ${RICHTEXT_HIGHLIGHT_COLORS.orange};
      }
      &-pink {
        background-color: ${RICHTEXT_HIGHLIGHT_COLORS.pink};
      }
      &-green {
        background-color: ${RICHTEXT_HIGHLIGHT_COLORS.green};
      }
      &-blue {
        background-color: ${RICHTEXT_HIGHLIGHT_COLORS.blue};
      }
      &-purple {
        background-color: ${RICHTEXT_HIGHLIGHT_COLORS.purple};
      }
    }

    p:has(+ .richtext-placeholder) {
      float: left;
      width: 1px;
    }

    .richtext-placeholder {
      color: #adb5bd;
      pointer-events: none;
      user-select: none;

      ${({ theme }) =>
        theme.name === 'main' &&
        css`
          color: ${({ theme }) => theme.color.neutral_40};
        `}
    }

    ${({ $hasPlannerEvents, $hasMultilinePlannerEvents }) =>
      $hasPlannerEvents &&
      css`
        &:first-line {
          line-height: 20px;
        }

        .planner-events {
          ${
            $hasMultilinePlannerEvents &&
            css`
            float: unset;
            margin-bottom: 4px;
          `
          }

          &:has(+ ol), &:has(+ ul), &:has(~ .richtext-placeholder > ol:first-child),  &:has(~ .richtext-placeholder > ul:first-child) {
            float: unset;
            margin-bottom: 4px;
          }

          & ~ .richtext-placeholder {
            line-height: 20px;

            p:first-line {
              line-height: 20px;
            }
          }
          & + p:first-line {
            line-height: 20px;
          }
        }
      `};

    .inline-suggestion {
      color: #999;
      position: relative;
      white-space: pre;

      ::after {
        position: absolute;
        left: calc(100% + 4px);
        height: 20px;
        width: 40px;
        content: " ";
        background-size: contain;
        background-repeat: no-repeat;
        background-position: right;
        background-image: url("data:image/png;base64,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");
      }
    }
  }
`;

const EditorContent = styled.div``;

const editorProps: EditorProps = {
  handleDOMEvents: {
    keydown: (_, event) => {
      if (event.key === 'Enter' && (event.metaKey || event.ctrlKey))
        return true;
    },
  },
};

export const RichTextEditor = observer(function RichTextEditor(
  props: RichTextEditorProps
): JSX.Element {
  const {
    content,
    disabled,
    asInput,
    autoFocus = false,
    onFocus,
    onBlur,
    onChange,
    id,
    getEditor,
    variant,
    showToolbarByDefault,
    placeholder,
    enableVideos,
    readOnly,
    disableFormatting,
    showEditorRef,
    'data-cy': dataCy,
    'data-name': dataName,
    disableLazyEditor = false,
    fetchAutocompletion,
    plannerEvents,
  } = props;
  const nonEditable = disabled || readOnly;
  const videoService = useVideoService();
  const richTextRef = useRef<HTMLDivElement>(null);
  const [isFocusWithinRichtext, setIsFocusWithinRichtext] = useState(false);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const [showEditor, setShowEditor] = useState<boolean>(false);
  const editorContentRef = useRef<HTMLDivElement>(null);
  const rawValueRef = useRef<HTMLDivElement>(null);
  const setInitialFocus = useRef<boolean>(false);
  const [hasMultilinePlannerEvents, setHasMultilinePlannerEvents] =
    useState(false);
  const editor = useEditor(
    {
      element: editorContentRef.current ?? undefined,
      extensions: [
        StarterKit.configure({
          bold: false,
        }),
        TextStyle,
        Highlight,
        Underline,
        VideoLink.configure({ videoService }),
        Link,
        YtdBold,
        Placeholder.configure({
          placeholder,
        }),
        ...(fetchAutocompletion
          ? [InlineSuggestion.configure({ fetchAutocompletion })]
          : []),
        PlannerEvents.configure({ events: plannerEvents }),
      ],
      content,
      editorProps,
      injectCSS: false,
      editable: !nonEditable,
      autofocus: autoFocus,
      onFocus: onFocus ?? (() => {}),
      onBlur: onBlur ?? (() => {}),
      onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    },
    showEditor,
    setInitialFocus.current
  );

  useEffect(() => {
    if (editor) {
      getEditor?.(editor);
    }
  }, [getEditor, editor]);

  useEffect(() => {
    if (showEditorRef) {
      showEditorRef.current = () => {
        setInitialFocus.current = true;
        setShowEditor(true);
      };
    }
  }, [showEditorRef]);

  useEffect(() => {
    if (disableLazyEditor) {
      setShowEditor(true);
    }
  }, [disableLazyEditor]);

  useEffect(() => {
    function updateContainerWidth() {
      const width = richTextRef.current?.getBoundingClientRect().width;
      if (width) {
        setContainerWidth(width);
      }
    }

    const observer = new ResizeObserver(updateContainerWidth);

    if (richTextRef.current) {
      observer.observe(richTextRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    const onFocusIn = () => {
      clearTimeout(timeout);
      setIsFocusWithinRichtext(true);
    };
    const onFocusOut = () => {
      timeout = setTimeout(() => {
        setIsFocusWithinRichtext(false);
      }, 10);
    };
    const element = richTextRef.current;
    element?.addEventListener('focusin', onFocusIn);
    element?.addEventListener('focusout', onFocusOut);

    return () => {
      element?.removeEventListener('focusin', onFocusIn);
      element?.removeEventListener('focusout', onFocusOut);
    };
  }, []);

  useEffect(() => {
    const rawValueDiv = rawValueRef.current;
    function clickHandler(event: MouseEvent) {
      if (event.button !== 0) {
        return false;
      }

      const link = (event.target as HTMLElement).closest('a');

      const className = link?.className;
      const href = link?.href;
      const videoLinkParts = href?.split('/');
      const videoId = videoLinkParts?.[videoLinkParts.length - 1];

      if (link && className?.includes('ytd-video') && videoId && videoService) {
        event.preventDefault();
        videoService.openVideoPlayer(videoId);
        return true;
      }
    }
    let links: NodeListOf<HTMLAnchorElement>;
    if (!showEditor && rawValueDiv && content) {
      links = rawValueDiv.querySelectorAll<HTMLAnchorElement>('a.ytd-video');
      links.forEach(link => link.addEventListener('click', clickHandler));
    }

    return () => {
      links?.forEach(link => link.removeEventListener('click', clickHandler));
    };
  }, [showEditor, videoService, content]);

  useEffect(() => {
    function checkPlannerEvents() {
      const eventsContainer =
        richTextRef.current?.querySelector<HTMLDivElement>('.planner-events');
      if (
        (eventsContainer?.clientHeight ?? 0) > 20 ||
        (eventsContainer?.clientWidth ?? 0) * 1.1 >=
          (richTextRef.current?.clientWidth ?? 0)
      ) {
        setHasMultilinePlannerEvents(true);
      } else {
        setHasMultilinePlannerEvents(false);
      }
    }
    const observer = new ResizeObserver(checkPlannerEvents);

    if (richTextRef.current && plannerEvents?.length) {
      checkPlannerEvents();
      observer.observe(richTextRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [plannerEvents]);

  return (
    <StyledRichText
      asInput={asInput}
      variant={variant}
      ref={richTextRef}
      aria-readonly={readOnly}
      disabled={disabled}
      data-cy={dataCy}
      data-name={dataName}
      data-active={showEditor}
      $hasPlannerEvents={Boolean((plannerEvents?.length ?? 0) > 0)}
      $hasMultilinePlannerEvents={hasMultilinePlannerEvents}
    >
      <EditorContent ref={editorContentRef} id={id} key={id}>
        {!showEditor && (
          <div
            ref={rawValueRef}
            contentEditable={!nonEditable}
            translate="no"
            tabIndex={0}
            className="tiptap ProseMirror"
            onClick={
              !nonEditable
                ? () => {
                    setInitialFocus.current = true;
                    setShowEditor(true);
                  }
                : undefined
            }
            onMouseEnter={
              (typeof window !== 'undefined' && 'ontouchstart' in window) ||
              nonEditable
                ? undefined
                : () => {
                    setShowEditor(true);
                  }
            }
            onFocus={
              !nonEditable
                ? () => {
                    setInitialFocus.current = true;
                    setShowEditor(true);
                  }
                : undefined
            }
            dangerouslySetInnerHTML={{
              __html: `${createLazyRichtextPlannerEvents(plannerEvents)}${
                content ||
                (placeholder
                  ? `<p></p><div class="richtext-placeholder">${placeholder}</div>`
                  : '<p><br class="ProseMirror-trailingBreak"></p>')
              }`,
            }}
          />
        )}
      </EditorContent>
      {!nonEditable && containerWidth && !disableFormatting && (
        <MenuBar
          containerWidth={containerWidth}
          editor={editor}
          variant={variant}
          showToolbarByDefault={showToolbarByDefault}
          enableVideos={enableVideos}
          showControls={isFocusWithinRichtext}
        />
      )}
    </StyledRichText>
  );
});
